// Module 19 (BL-380) DC-1 - the Events board's numbers: tenant-day maths, the status-pill counts, the KPI tiles, the RSVP words on a card,
// and the CSV export. No network, no React. Dates are 'YYYY-MM-DD' calendar days compared as text (they sort the same as dates).

// The company time zone is the one the database already uses (org_time_zone(): staff-alert zone, else the assistant's, else New York).
export const DEFAULT_TENANT_ZONE = 'America/New_York';
export const KPI_WINDOW_DAYS = 30;

const pad = (n) => String(n).padStart(2, '0');

export function validZone(zone) {
    if (typeof zone !== 'string' || !zone.trim()) return null;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone.trim() });
        return zone.trim();
    } catch {
        return null;
    }
}

// Today's calendar day in the tenant's time zone, as 'YYYY-MM-DD' (the browser's own zone is not used).
export function todayKey(zone = DEFAULT_TENANT_ZONE, now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: validZone(zone) || DEFAULT_TENANT_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const pick = (type) => parts.find((part) => part.type === type)?.value;
    return `${pick('year')}-${pick('month')}-${pick('day')}`;
}

export function addDaysKey(key, days) {
    const [year, month, day] = String(key).split('-').map(Number);
    const moved = new Date(Date.UTC(year, month - 1, day + days));
    return `${moved.getUTCFullYear()}-${pad(moved.getUTCMonth() + 1)}-${pad(moved.getUTCDate())}`;
}

const dayOf = (event) => String(event?.event_date || '').slice(0, 10);
export const statusOf = (event) => event?.engagement?.status || 'requested';
export const isCanceled = (event) => statusOf(event) === 'canceled';
export const isUpcomingOn = (event, today) => dayOf(event) !== '' && dayOf(event) >= today;

// ---------------------------------------------------------------- RSVP numbers per event
// `summary` is a Map of engagement_id -> { invited, yes, no, maybe, noReply } (hosts only; plus-ones are heads, not invitations - Q-031),
// or null when the numbers could not be loaded. An event with no guests has no entry.
export const NO_GUESTS = Object.freeze({ invited: 0, yes: 0, no: 0, maybe: 0, noReply: 0 });

export function rsvpSummaryMap(rows) {
    const map = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        if (!row?.engagement_id) return;
        map.set(row.engagement_id, {
            invited: Number(row.invited) || 0,
            yes: Number(row.yes_count) || 0,
            no: Number(row.no_count) || 0,
            maybe: Number(row.maybe_count) || 0,
            noReply: Number(row.no_reply_count) || 0,
        });
    });
    return map;
}

export const summaryFor = (summary, event) => (summary && summary.get(event?.engagement_id)) || NO_GUESTS;

// "In": the guests who have answered (Yes, No or Maybe). Only "No reply" has not.
export const answeredOf = (counts) => counts.yes + counts.no + counts.maybe;
export const percentOf = (answered, invited) => (invited > 0 ? Math.round((answered / invited) * 100) : null);
export const responsePercent = (counts) => percentOf(answeredOf(counts), counts.invited);

export function rsvpProgress(counts) {
    const answered = answeredOf(counts);
    const percent = percentOf(answered, counts.invited);
    return {
        empty: counts.invited === 0,
        answered,
        invited: counts.invited,
        percent,
        text: counts.invited === 0 ? 'No guests yet' : `${answered}/${counts.invited} · ${percent}%`,
        width: percent === null ? 0 : percent,
    };
}

// ---------------------------------------------------------------- status pills and KPI tiles
// The three status pills count the events that match the date filter and the search (so a pill's number is what clicking it shows).
export function matchesSearch(event, search) {
    const needle = String(search || '').trim().toLowerCase();
    if (!needle) return true;
    return [event.event_name, event.venue_name, event.venue_address].some((value) => String(value || '').toLowerCase().includes(needle));
}

export function matchesWhen(event, whenFilter, today) {
    if (whenFilter === 'upcoming') return isUpcomingOn(event, today);
    if (whenFilter === 'past') return !isUpcomingOn(event, today);
    return true;
}

export function statusCounts(events, { whenFilter = 'upcoming', search = '', today }) {
    const scoped = events.filter((event) => matchesWhen(event, whenFilter, today) && matchesSearch(event, search));
    const canceled = scoped.filter(isCanceled).length;
    return { all: scoped.length, planned: scoped.length - canceled, canceled };
}

// Events next 30 days: planned events dated today through today + 29. Guests invited / response rate: the planned upcoming events
// (today onward), summed. The rate is answered / invited as a whole percent, or null (shown as a dash) when nobody is invited.
export function boardKpis(events, summary, today) {
    const planned = events.filter((event) => !isCanceled(event));
    const windowEnd = addDaysKey(today, KPI_WINDOW_DAYS - 1);
    const next30 = planned.filter((event) => dayOf(event) >= today && dayOf(event) <= windowEnd).length;
    const upcoming = planned.filter((event) => isUpcomingOn(event, today));
    if (!summary) return { next30, invited: null, answered: null, rate: null, available: false };
    const totals = upcoming.reduce((sum, event) => {
        const counts = summaryFor(summary, event);
        return { invited: sum.invited + counts.invited, answered: sum.answered + answeredOf(counts) };
    }, { invited: 0, answered: 0 });
    return { next30, invited: totals.invited, answered: totals.answered, rate: percentOf(totals.answered, totals.invited), available: true };
}

// ---------------------------------------------------------------- CSV export
export const EXPORT_COLUMNS = ['Event', 'Date', 'Start', 'End', 'Venue', 'Type', 'Status', 'Invited', 'Yes', 'No', 'Maybe', 'No reply', 'Guests expected'];

// A spreadsheet runs a cell that starts with = + - @ (or a tab / carriage return) as a formula. Text cells get a leading quote so it is shown as text.
export function guardCsvText(value) {
    const text = String(value ?? '');
    return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
}

function csvCell(value) {
    const text = typeof value === 'number' ? String(value) : guardCsvText(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const hhmm = (value) => String(value || '').slice(0, 5);

// One row per event shown. `labels` = { typeLabel(key), statusLabel(status) } so the file says what the screen says.
export function eventsCsv(events, summary, labels) {
    const rows = events.map((event) => {
        const counts = summaryFor(summary, event);
        return [
            event.event_name, dayOf(event), hhmm(event.start_time), hhmm(event.end_time), event.venue_name || '',
            labels.typeLabel(event.event_type), labels.statusLabel(statusOf(event)),
            counts.invited, counts.yes, counts.no, counts.maybe, counts.noReply,
            event.expected_guest_count ?? '',
        ];
    });
    return `\uFEFF${[EXPORT_COLUMNS, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')}\r\n`;
}

export const eventsCsvFileName = (today) => `events-${today}.csv`;

export function downloadCsv(fileName, text) {
    try {
        const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        return true;
    } catch {
        return false;
    }
}
